import {
	getScoreContext,
	getScoreExplainer,
	getScoreSupportText,
} from '#app/utils/consensus.shared.ts'

type Breakdown = {
	critic: number
	consensus: number
	volume: number
	google: number
	boost: number
	hiddenGem: number
	signalCount: number
	weightedAvg: number
	topCritic: { name: string; score: number } | null
}

type NumericKey = 'critic' | 'consensus' | 'volume' | 'google' | 'boost'

type Props = {
	score: number
	breakdown: Breakdown
	confidence?: number
	eliteCount?: number
	editorialCount?: number
	googleRating?: number
	totalReviews?: number
	socialMentions?: number
	riskNote?: string | null
}

const SEGMENTS: {
	key: NumericKey
	label: string
	desc: string
	color: string
}[] = [
	{
		key: 'critic',
		label: 'Editorial Weight',
		desc: 'Authority + depth + recency',
		color: '#22c55e',
	},
	{
		key: 'consensus',
		label: 'Agreement',
		desc: 'How aligned the critics are',
		color: '#00e0ff',
	},
	{
		key: 'volume',
		label: 'Coverage',
		desc: 'How many real review signals exist',
		color: '#a855f7',
	},
	{
		key: 'google',
		label: 'Public Signal',
		desc: 'Google rating + volume',
		color: '#f59e0b',
	},
	{
		key: 'boost',
		label: 'Boosts',
		desc: 'Manual boosts + hidden gem',
		color: '#ec4899',
	},
]

function clampPct(value: number) {
	if (Number.isNaN(value) || value <= 0) return 4
	return Math.max(4, Math.min(100, Math.round(value)))
}

export function ConsensusCard({
	score,
	breakdown,
	confidence,
	eliteCount,
	editorialCount,
	googleRating,
	totalReviews,
	socialMentions,
	riskNote,
}: Props) {
	const context = getScoreContext(score)
	const totalPoints =
		breakdown.critic +
		breakdown.consensus +
		breakdown.volume +
		breakdown.google +
		(breakdown.boost + breakdown.hiddenGem)

	return (
		<section className="card rounded-xl border border-[#1a1a2e] bg-[#0d0d15] shadow-[0_0_24px_rgba(0,224,255,0.12)]">
			<div className="card-body space-y-4 text-[#f8f8ff]">
				<div className="flex items-start justify-between gap-3">
					<div>
						<p className="text-xs tracking-[0.18em] text-[#7de9ff] uppercase">
							Signal Score
						</p>
						<div className="flex items-center gap-3">
							<span
								className="rounded-lg px-3 py-1.5 text-4xl font-black"
								style={{
									color: context.color,
									backgroundColor: `${context.color}12`,
								}}
							>
								{score}
							</span>
							<div className="flex flex-col text-sm">
								<span
									className="font-semibold"
									style={{ color: context.color }}
								>
									{context.label}
								</span>
								<span className="text-[#b0b0e0]">
									{getScoreSupportText(score)}
								</span>
								{typeof confidence === 'number' && (
									<span className="text-[#7070a0]">
										Agreement {Math.round(confidence * 100)}%
									</span>
								)}
								<span className="text-xs text-[#7070a0]">
									{eliteCount ?? 0} Elite · {editorialCount ?? 0} Editorial ·{' '}
									{socialMentions ?? 0} mentions
								</span>
							</div>
						</div>
					</div>
					<div className="max-w-[200px] space-y-1 text-right text-xs text-[#b0b0e0]">
						<p>{getScoreExplainer(score)}</p>
						{breakdown.topCritic && (
							<p className="text-[#7de9ff]">
								Top critic: {breakdown.topCritic.name} (
								{Math.round(breakdown.topCritic.score * 100)}%)
							</p>
						)}
						{riskNote && <p className="text-[#ff9f1c]">⚠ {riskNote}</p>}
					</div>
				</div>

				{/* Segmented contribution bar */}
				<div className="space-y-2">
					<div className="flex w-full overflow-hidden rounded-full border border-[#1a1a2e] bg-[#0a0a12]">
						{SEGMENTS.map((segment) => {
							const value =
								segment.key === 'boost'
									? (breakdown.boost ?? 0) + (breakdown.hiddenGem ?? 0)
									: (breakdown[segment.key] ?? 0)
							const pct =
								totalPoints > 0 ? clampPct((value / totalPoints) * 100) : 0
							return (
								<div
									key={segment.key}
									className="h-3"
									style={{
										width: `${pct}%`,
										backgroundColor: `${segment.color}cc`,
									}}
									title={`${segment.label}: ${value} pts`}
								/>
							)
						})}
					</div>
					<div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
						{SEGMENTS.map((segment) => (
							<div key={segment.key} className="flex items-start gap-2">
								<span
									className="mt-1 h-2 w-2 rounded-sm"
									style={{ backgroundColor: segment.color }}
									aria-hidden
								/>
								<div className="text-xs">
									<p className="font-semibold" style={{ color: segment.color }}>
										{segment.label}{' '}
										{segment.key === 'boost'
											? (breakdown.boost ?? 0) + (breakdown.hiddenGem ?? 0)
											: breakdown[segment.key]}{' '}
										pts
									</p>
									<p className="text-[11px] text-[#b0b0e0]">{segment.desc}</p>
								</div>
							</div>
						))}
					</div>
				</div>

				{/* Stats */}
				<div className="grid gap-3 sm:grid-cols-2">
					<div className="rounded-lg border border-[#1a1a2e] bg-[#0a0a12] p-3">
						<p className="text-[11px] tracking-[0.16em] text-[#7070a0] uppercase">
							Review signals
						</p>
						<p className="text-sm text-[#f8f8ff]">
							{eliteCount ?? 0} Elite · {editorialCount ?? 0} Editorial ·{' '}
							{breakdown.signalCount} total signals
						</p>
					</div>
					<div className="rounded-lg border border-[#1a1a2e] bg-[#0a0a12] p-3">
						<p className="text-[11px] tracking-[0.16em] text-[#7070a0] uppercase">
							Public signal
						</p>
						<p className="text-sm text-[#f8f8ff]">
							{googleRating ? `${googleRating.toFixed(1)}★` : '—'} (
							{totalReviews?.toLocaleString() ?? '0'} reviews)
						</p>
					</div>
				</div>
			</div>
		</section>
	)
}
